import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../config/axios';

interface Product {
  id: string;
  name_en: string;
  name_zh: string;
  description_en: string;
  description_zh: string;
  price_per_kg: number;
  minimum_order_quantity: number;
  order_increment: number | null;
  image_filename: string | null;
  is_active: boolean;
  created_at: string;
}

interface ProductInput {
  name_en: string;
  name_zh: string;
  description_en: string;
  description_zh: string;
  price_per_kg: number;
  minimum_order_quantity: number;
  order_increment: number | null;
  image_filename: string;
  is_active: boolean;
}

const ProductManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const language = localStorage.getItem('language') || 'en';
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<ProductInput>({
    name_en: '',
    name_zh: '',
    description_en: '',
    description_zh: '',
    price_per_kg: 0,
    minimum_order_quantity: 0.5,
    order_increment: null,
    image_filename: '',
    is_active: true,
  });
  const [error, setError] = useState<string>('');

  // Fetch all products (including inactive)
  const { data: products, isLoading } = useQuery<Product[]>({
    queryKey: ['adminProducts'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/admin/products');
      return response.data;
    },
  });

  // Create product mutation
  const createMutation = useMutation({
    mutationFn: async (data: ProductInput) => {
      const response = await axiosInstance.post('/api/admin/products', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminProducts'] });
      queryClient.invalidateQueries({ queryKey: ['activeProducts'] });
      resetForm();
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create product');
    },
  });

  // Update product mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ProductInput }) => {
      const response = await axiosInstance.put(`/api/admin/products/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminProducts'] });
      queryClient.invalidateQueries({ queryKey: ['activeProducts'] });
      resetForm();
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to update product');
    },
  });

  // Toggle product status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const response = await axiosInstance.patch(`/api/admin/products/${id}/status`, { is_active });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminProducts'] });
      queryClient.invalidateQueries({ queryKey: ['activeProducts'] });
    },
    onError: (err: any) => {
      alert(err.response?.data?.detail || 'Failed to update product status');
    },
  });

  // Delete product mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`/api/admin/products/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminProducts'] });
      queryClient.invalidateQueries({ queryKey: ['activeProducts'] });
    },
    onError: (err: any) => {
      alert(err.response?.data?.detail || 'Failed to delete product');
    },
  });

  const resetForm = () => {
    setFormData({
      name_en: '',
      name_zh: '',
      description_en: '',
      description_zh: '',
      price_per_kg: 0,
      minimum_order_quantity: 0.5,
      order_increment: null,
      image_filename: '',
      is_active: true,
    });
    setEditingProduct(null);
    setShowForm(false);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name_en: product.name_en,
      name_zh: product.name_zh,
      description_en: product.description_en,
      description_zh: product.description_zh,
      price_per_kg: product.price_per_kg,
      minimum_order_quantity: product.minimum_order_quantity,
      order_increment: product.order_increment,
      image_filename: product.image_filename || '',
      is_active: product.is_active,
    });
    setShowForm(true);
    setError('');
  };

  const handleToggleStatus = (product: Product) => {
    toggleStatusMutation.mutate({ id: product.id, is_active: !product.is_active });
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this product? This can only be done if there are no orders.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate form
    if (!formData.name_en.trim() || !formData.name_zh.trim()) {
      setError('Product names in both languages are required');
      return;
    }

    if (formData.price_per_kg <= 0) {
      setError('Price must be greater than 0');
      return;
    }

    if (formData.minimum_order_quantity <= 0) {
      setError('Minimum order quantity must be greater than 0');
      return;
    }

    if (formData.order_increment !== null && formData.order_increment <= 0) {
      setError('Order increment must be greater than 0');
      return;
    }

    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>Product Management</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#ffc107',
                color: '#333',
                border: 'none',
                borderRadius: '4px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              + Add Product
            </button>
          )}
          <a
            href="/admin"
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
            }}
          >
            ← Back to Dashboard
          </a>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <section style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#fff9e6', borderRadius: '8px', border: '2px solid #ffc107' }}>
          <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
          
          {error && (
            <div style={{ padding: '10px', marginBottom: '15px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', border: '1px solid #f5c6cb' }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              {/* Product Name English */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Product Name (English) *
                </label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                  required
                  placeholder="e.g., Crawfish"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}
                />
              </div>

              {/* Product Name Chinese */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Product Name (Chinese) *
                </label>
                <input
                  type="text"
                  value={formData.name_zh}
                  onChange={(e) => setFormData({ ...formData, name_zh: e.target.value })}
                  required
                  placeholder="e.g., 小龙虾"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}
                />
              </div>

              {/* Price per KG */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Price per KG (€) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price_per_kg}
                  onChange={(e) => setFormData({ ...formData, price_per_kg: parseFloat(e.target.value) || 0 })}
                  required
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}
                />
              </div>

              {/* Minimum Order Quantity */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Minimum Order Quantity (kg) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={formData.minimum_order_quantity}
                  onChange={(e) => setFormData({ ...formData, minimum_order_quantity: parseFloat(e.target.value) || 0 })}
                  required
                  placeholder="0.5"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Minimum quantity customers must order
                </small>
              </div>

              {/* Order Increment */}
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Order Increment (kg)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={formData.order_increment || ''}
                  onChange={(e) => setFormData({ ...formData, order_increment: e.target.value ? parseFloat(e.target.value) : null })}
                  placeholder="Optional (e.g., 0.5)"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                  }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Quantity must be in multiples of this value
                </small>
              </div>
            </div>

            {/* Description English */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Description (English)
              </label>
              <textarea
                value={formData.description_en}
                onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                placeholder="Product description in English..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            {/* Description Chinese */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Description (Chinese)
              </label>
              <textarea
                value={formData.description_zh}
                onChange={(e) => setFormData({ ...formData, description_zh: e.target.value })}
                placeholder="Product description in Chinese..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            {/* Image Filename */}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Image Filename
              </label>
              <input
                type="text"
                value={formData.image_filename}
                onChange={(e) => setFormData({ ...formData, image_filename: e.target.value })}
                placeholder="e.g., crawfish.jpg (leave empty for no image)"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}
              />
              <small style={{ color: '#666', fontSize: '12px' }}>
                Place images in frontend/public/images/ folder. Enter just the filename here.
              </small>
            </div>

            {/* Status */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: 'bold' }}>Active (visible to customers)</span>
              </label>
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                style={{
                  padding: '10px 30px',
                  backgroundColor: '#ffc107',
                  color: '#333',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editingProduct ? 'Update' : 'Create')}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: '10px 30px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      {/* Products List */}
      <section>
        <h2>Product Catalog</h2>
        
        {products && products.length > 0 ? (
          <div style={{ display: 'grid', gap: '20px' }}>
            {products.map(product => (
              <div
                key={product.id}
                style={{
                  padding: '20px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: product.is_active ? 'white' : '#f8f9fa',
                  opacity: product.is_active ? 1 : 0.7,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '20px' }}>
                  {/* Product Image */}
                  {product.image_filename && (
                    <div style={{ flexShrink: 0 }}>
                      <img
                        src={`/images/${product.image_filename}`}
                        alt={language === 'zh' ? product.name_zh : product.name_en}
                        style={{
                          width: '120px',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Product Info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <h3 style={{ margin: 0 }}>
                        {language === 'zh' ? product.name_zh : product.name_en}
                      </h3>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: product.is_active ? '#28a745' : '#6c757d',
                        color: 'white',
                      }}>
                        {product.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                    
                    <p style={{ margin: '10px 0', color: '#666', fontSize: '14px' }}>
                      {(language === 'zh' ? product.description_zh : product.description_en) || 'No description provided'}
                    </p>
                    
                    <div style={{ display: 'flex', gap: '20px', marginTop: '10px', flexWrap: 'wrap' }}>
                      <p style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#007bff' }}>
                        €{product.price_per_kg.toFixed(2)} / kg
                      </p>
                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                        Min: {product.minimum_order_quantity} kg
                      </p>
                      {product.order_increment && (
                        <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                          Increment: {product.order_increment} kg
                        </p>
                      )}
                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                        Created: {new Date(product.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginLeft: '20px' }}>
                    <button
                      onClick={() => handleEdit(product)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Edit
                    </button>
                    
                    <button
                      onClick={() => handleToggleStatus(product)}
                      disabled={toggleStatusMutation.isPending}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: product.is_active ? '#6c757d' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {product.is_active ? 'Disable' : 'Enable'}
                    </button>
                    
                    <button
                      onClick={() => handleDelete(product.id)}
                      disabled={deleteMutation.isPending}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <p style={{ fontSize: '18px', color: '#666', marginBottom: '20px' }}>
              No products in the catalog yet.
            </p>
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: '12px 30px',
                backgroundColor: '#ffc107',
                color: '#333',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              Add First Product
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default ProductManagementPage;
